import React, { useState } from "react";
import { Box, CircularProgress, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";

import { jobService } from "../services/jobService";
import { light as C, radius } from "../../hiring/theme";

// Whether this job is taking candidates.
//
// WHY THIS IS A CHIP AND NOT A SWITCH
// There are three states, not two. A toggle would force Draft to hide
// somewhere else, and "draft" is a real thing — a job written up but not open
// yet, which should not be collecting applicants by accident.
//
// WHY CLOSING SAYS WHAT IT DOES
// Closing is not cosmetic any more: the public form and bulk upload both start
// refusing. Somebody who thinks they are tidying a list needs to know they are
// shutting a door, so the menu item says so rather than just naming the state.

const STATES = {
  ACTIVE: {
    label: "Open",
    hint: "Accepting candidates",
    fg: "#047857",
    bg: "rgba(4,120,87,.10)",
    dot: "#059669",
  },
  CLOSED: {
    label: "Closed",
    hint: "Not accepting candidates",
    fg: "#b91c1c",
    bg: "rgba(185,28,28,.09)",
    dot: "#dc2626",
  },
  DRAFT: {
    label: "Draft",
    hint: "Not published yet",
    fg: "#a16207",
    bg: "rgba(161,98,7,.10)",
    dot: "#ca8a04",
  },
};

const CHOICES = [
  { value: "ACTIVE", title: "Open", body: "Candidates can apply through the form and you can upload resumes." },
  { value: "CLOSED", title: "Closed", body: "New applications are refused. Everyone already in the pipeline stays." },
  { value: "DRAFT", title: "Draft", body: "Not published. Nothing arrives and nothing is sent." },
];

export default function JobStatusControl({ jobId, status, onChange, readOnly = false }) {
  const [anchor, setAnchor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const current = STATES[status] || STATES.DRAFT;

  const pick = async (next) => {
    setAnchor(null);
    if (next === status) return;
    setSaving(true);
    setError("");
    try {
      const res = await jobService.setJobStatus(jobId, next);
      if (res?.data?.success) onChange?.(next, res.data.statusMessage);
      else setError(res?.data?.statusMessage || "Could not change the status");
    } catch (e) {
      setError(e?.response?.data?.statusMessage || "Could not change the status");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Tooltip title={error || current.hint}>
        <Box
          component={readOnly ? "div" : "button"}
          onClick={readOnly ? undefined : (e) => setAnchor(e.currentTarget)}
          disabled={saving}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.25,
            py: 0.5,
            border: `1px solid ${error ? "#dc2626" : "transparent"}`,
            borderRadius: radius.pill || 999,
            bgcolor: current.bg,
            color: current.fg,
            font: "inherit",
            fontSize: 12.5,
            fontWeight: 700,
            cursor: readOnly ? "default" : "pointer",
            transition: "filter .12s",
            "&:hover": readOnly ? {} : { filter: "brightness(.96)" },
          }}
        >
          {saving ? (
            <CircularProgress size={11} sx={{ color: current.fg }} />
          ) : (
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: current.dot }} />
          )}
          {current.label}
          {!readOnly && <ExpandMoreRoundedIcon sx={{ fontSize: 15, opacity: 0.7 }} />}
        </Box>
      </Tooltip>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{ sx: { borderRadius: radius.md, minWidth: 268, mt: 0.5 } }}
      >
        {CHOICES.map((c) => (
          <MenuItem
            key={c.value}
            selected={c.value === status}
            onClick={() => pick(c.value)}
            sx={{ display: "block", py: 1, px: 1.75, whiteSpace: "normal" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: STATES[c.value].dot }} />
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: C.ink }}>
                {c.title}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 12, color: C.muted, mt: 0.25, lineHeight: 1.45, pl: 2 }}>
              {c.body}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
